import { deflateSync, inflateSync, strFromU8, strToU8 } from 'fflate';
import config from '../config';

export const CART_API_VERSION = 1;
export const DEFAULT_CART_COOKIE_NAME = 'crate_marketplace_cart_v1';
export const CART_COOKIE_NAME = String(config.marketplaceCartCookieKey ?? '').trim() || DEFAULT_CART_COOKIE_NAME;
export const LEGACY_CART_COOKIE_NAMES = CART_COOKIE_NAME === DEFAULT_CART_COOKIE_NAME
    ? []
    : [DEFAULT_CART_COOKIE_NAME];
export const CART_COOKIE_VERSION = 2;
export const CART_LINE_LIMIT = 50;
export const CART_COOKIE_SIZE_LIMIT = 3500;
const COMPRESSED_CART_COOKIE_PREFIX = 'v2.';
export const CART_SYNC_STATES = {
    CLEAN: 'clean',
    DIRTY: 'dirty',
    SYNCING: 'syncing',
    ERROR: 'error',
};
export const PRIMARY_MARKETPLACE_ORIGIN = 'https://user.productioncrate.com';
const SECONDARY_CRATE_DOMAINS = [
    'footagecrate.com',
    'soundscrate.com',
    'rendercrate.com',
    'graphicscrate.com',
];
const SECONDARY_CONTENT_HOSTS = {
    'footagecrate.com': 'vfx.productioncrate.com',
    'soundscrate.com': 'sfx.productioncrate.com',
    'rendercrate.com': 'productioncrate.com',
    'graphicscrate.com': 'graphics.productioncrate.com',
};
export const MARKETPLACE_CART_RETURN_PARAM = 'marketplace_cart_return';
const CHUNK_BYTE_LENGTH = 0x8000;

const toBase64 = (bytes) => {
    let binary = '';

    for (let index = 0; index < bytes.length; index += CHUNK_BYTE_LENGTH) {
        const chunk = bytes.subarray(index, index + CHUNK_BYTE_LENGTH);
        binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
};

const fromBase64 = (value) => {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
};

const toBase64Url = (bytes) => toBase64(bytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');

const fromBase64Url = (value) => {
    const normalizedValue = String(value ?? '')
        .replace(/-/g, '+')
        .replace(/_/g, '/');
    const paddingLength = (4 - (normalizedValue.length % 4)) % 4;

    return fromBase64(`${normalizedValue}${'='.repeat(paddingLength)}`);
};

const TYPE_LABELS = {
    asset: 'Asset',
    bundle: 'Bundle',
};

const DEFAULT_MARKETPLACE_CURRENCY = 'USD';
const CART_PATH_VERTICALS = {
    'video-effects': 'VFX',
    'motion-graphics': 'VFX',
    'stock-image-hd': 'GRA',
    'graphics': 'GRA',
    'textures': 'GRA',
    'sounds-effects': 'SFX',
    'royalty-free-music': 'SFX',
    'objects': '3D',
    'materials': '3D',
    'environments': '3D',
};

const normalizeCartVertical = (value) => {
    const normalizedValue = String(value ?? '').trim().toLowerCase();

    if (!normalizedValue) {
        return null;
    }

    if (
        normalizedValue === 'vfx'
        || normalizedValue.startsWith('vfx/')
        || normalizedValue === 'footagecrate'
        || normalizedValue === 'vfx.productioncrate.com'
        || normalizedValue === 'footagecrate.com'
        || normalizedValue.endsWith('.footagecrate.com')
        || normalizedValue.includes('visual effect')
        || normalizedValue.includes('visual-effect')
    ) {
        return 'VFX';
    }

    if (
        normalizedValue === 'sfx'
        || normalizedValue === 'sounds'
        || normalizedValue === 'audio'
        || normalizedValue === 'music'
        || normalizedValue === 'soundscrate'
        || normalizedValue === 'sfx.productioncrate.com'
        || normalizedValue === 'soundscrate.com'
        || normalizedValue.endsWith('.soundscrate.com')
        || normalizedValue.includes('sound effect')
        || normalizedValue.includes('sound-effect')
    ) {
        return 'SFX';
    }

    if (
        normalizedValue === '3d'
        || normalizedValue === 'rendercrate'
        || normalizedValue === '3d.productioncrate.com'
        || normalizedValue === 'rendercrate.com'
        || normalizedValue.endsWith('.rendercrate.com')
        || normalizedValue.includes('render')
        || normalizedValue.includes('3d model')
    ) {
        return '3D';
    }

    if (
        normalizedValue === 'gra'
        || normalizedValue === 'graphics'
        || normalizedValue === 'graphicscrate'
        || normalizedValue === 'graphics.productioncrate.com'
        || normalizedValue === 'graphicscrate.com'
        || normalizedValue.endsWith('.graphicscrate.com')
        || normalizedValue.includes('graphic')
    ) {
        return 'GRA';
    }

    return null;
};

const inferCartVerticalFromUrl = (value) => {
    const rawValue = String(value ?? '').trim();

    if (!rawValue) {
        return null;
    }

    try {
        const parsedUrl = new URL(rawValue);
        return normalizeCartVertical(parsedUrl.searchParams.get('main_category'))
            || CART_PATH_VERTICALS[String(parsedUrl.pathname ?? '')
                .split('/')
                .filter(Boolean)[0]?.toLowerCase?.()]
            || normalizeCartVertical(parsedUrl.hostname);
    } catch (error) {
        return normalizeCartVertical(rawValue);
    }
};

const getNormalizedUrlOrigin = (value, normalizeUrl) => {
    const normalizedValue = normalizeUrl(value);
    if (!normalizedValue || normalizedValue.startsWith('/')) {
        return null;
    }

    try {
        return new URL(normalizedValue).origin;
    } catch (error) {
        return null;
    }
};

const getAssetUrlOrigin = (value) => getNormalizedUrlOrigin(value, normalizeAssetHref);

const getPreviewUrlOrigin = (value) => getNormalizedUrlOrigin(value, normalizePreviewUrl);

const compactSharedUrl = (value, sharedOrigin, normalizeUrl) => {
    const normalizedValue = normalizeUrl(value);
    if (!normalizedValue) {
        return undefined;
    }

    if (!sharedOrigin || normalizedValue.startsWith('/')) {
        return normalizedValue;
    }

    try {
        const parsedUrl = new URL(normalizedValue);
        if (parsedUrl.origin !== sharedOrigin) {
            return normalizedValue;
        }

        const pathWithQuery = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        return pathWithQuery || '/';
    } catch (error) {
        return normalizedValue;
    }
};

const compactAssetHref = (value, sharedOrigin) => compactSharedUrl(value, sharedOrigin, normalizeAssetHref);

const compactPreviewUrl = (value, sharedOrigin) => compactSharedUrl(value, sharedOrigin, normalizePreviewUrl);

const expandSharedUrl = (value, sharedOrigin, normalizeUrl) => {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
        return null;
    }

    if (sharedOrigin && rawValue.startsWith('/')) {
        return normalizeUrl(`${sharedOrigin}${rawValue}`);
    }

    return normalizeUrl(rawValue);
};

const expandAssetHref = (value, sharedOrigin) => expandSharedUrl(value, sharedOrigin, normalizeAssetHref);

const expandPreviewUrl = (value, sharedOrigin) => expandSharedUrl(value, sharedOrigin, normalizePreviewUrl);

const resolveCartVertical = (display, sourceContext = {}) => (
    normalizeCartVertical(
        display?.vertical
        ?? display?.vertical_tag
        ?? display?.verticalTag
        ?? display?.main_category
        ?? display?.mainCategory
    )
    || inferCartVerticalFromUrl(sourceContext?.originUrl)
    || inferCartVerticalFromUrl(sourceContext?.assetHref)
    || normalizeCartVertical(sourceContext?.originDomain)
    || normalizeCartVertical(
        display?.category
        ?? display?.category_name
        ?? display?.categoryName
        ?? display?.collection
        ?? display?.library
    )
);

const isPositiveInteger = (value) => Number.isInteger(value) && value > 0;

const parseInteger = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.trunc(value);
    }

    if (typeof value === 'string' && value.trim()) {
        const parsedValue = Number.parseInt(value, 10);
        if (Number.isFinite(parsedValue)) {
            return parsedValue;
        }
    }

    return null;
};

const normalizeIsoDate = (value, fallbackValue = new Date().toISOString()) => {
    if (typeof value !== 'string' || !value.trim()) {
        return fallbackValue;
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return fallbackValue;
    }

    return parsedDate.toISOString();
};

const normalizeAssetHref = (value) => {
    const rawValue = String(value ?? '').trim();

    if (!rawValue) {
        return null;
    }

    try {
        const parsedUrl = new URL(rawValue);
        if (['http:', 'https:'].includes(parsedUrl.protocol)) {
            return parsedUrl.toString();
        }
    } catch (error) {
        return null;
    }

    return null;
};

const normalizePreviewUrl = (value) => {
    const rawValue = String(value ?? '').trim();

    if (!rawValue) {
        return null;
    }

    if (rawValue.startsWith('/')) {
        return rawValue;
    }

    try {
        const parsedUrl = new URL(rawValue);
        if (['http:', 'https:'].includes(parsedUrl.protocol)) {
            return parsedUrl.toString();
        }
    } catch (error) {
        return null;
    }

    return null;
};

const buildPreviewUrlFromPreview = (preview) => {
    if (!preview || typeof preview !== 'object') {
        return null;
    }

    return normalizePreviewUrl(preview.url ?? preview.preview_url ?? `${preview.host || ''}${preview.path || ''}`);
};

const pickPreviewUrl = (previews) => {
    const previewList = Array.isArray(previews) ? previews : [];
    const imagePreviews = previewList.filter((preview) => String(preview?.type ?? '').toLowerCase() === 'image');
    const preference = ['lg', 'front', 'sprite', '2x', 'sm', 'xs', 'waveform'];

    for (const variant of preference) {
        const preview = imagePreviews.find((candidate) => String(candidate?.variant ?? '').toLowerCase() === variant);
        const previewUrl = buildPreviewUrlFromPreview(preview);
        if (previewUrl) {
            return previewUrl;
        }
    }

    return buildPreviewUrlFromPreview(imagePreviews[0]);
};

const resolveCartPreviewUrl = (display) => normalizePreviewUrl(
    display?.previewUrl
    ?? display?.preview_url
    ?? display?.thumbnailUrl
    ?? display?.thumbnail_url
    ?? display?.imageUrl
    ?? display?.image_url
    ?? display?.posterUrl
    ?? display?.poster_url
) || pickPreviewUrl(display?.previews);

const pickWaveformUrl = (previews) => {
    const previewList = Array.isArray(previews) ? previews : [];
    const waveformPreview = previewList.find((preview) => (
        String(preview?.type ?? '').toLowerCase() === 'json'
        && String(preview?.variant ?? '').toLowerCase() === 'waveform'
    ));

    return buildPreviewUrlFromPreview(waveformPreview);
};

const resolveCartWaveformUrl = (display) => normalizePreviewUrl(
    display?.waveformUrl
    ?? display?.waveform_url
    ?? display?.waveformJsonUrl
    ?? display?.waveform_json_url
) || pickWaveformUrl(display?.previews);

export const normalizeCurrency = (value) => {
    const normalizedValue = String(value ?? '').trim().toUpperCase();
    return normalizedValue || null;
};

export const formatCurrency = (amount, currency) => {
    const numericAmount = Number(amount);
    const normalizedCurrency = normalizeCurrency(currency);

    if (!Number.isFinite(numericAmount) || !normalizedCurrency) {
        return null;
    }

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: normalizedCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numericAmount);
    } catch (error) {
        return `${normalizedCurrency} ${numericAmount.toFixed(2)}`;
    }
};

export const isPrimaryProductioncrateHost = (hostname) => {
    const normalizedHostname = String(hostname ?? '').trim().toLowerCase();

    if (!normalizedHostname) {
        return false;
    }

    return normalizedHostname === 'productioncrate.com'
        || normalizedHostname.endsWith('.productioncrate.com')
        || normalizedHostname === 'localhost'
        || normalizedHostname === '127.0.0.1'
        || normalizedHostname.endsWith('.localhost');
};

export const isSecondaryCrateHost = (hostname) => {
    const normalizedHostname = String(hostname ?? '').trim().toLowerCase();

    return SECONDARY_CRATE_DOMAINS.some((domain) => (
        normalizedHostname === domain || normalizedHostname.endsWith(`.${domain}`)
    ));
};

const getSecondaryCrateDomain = (hostname) => {
    const normalizedHostname = String(hostname ?? '').trim().toLowerCase();

    return SECONDARY_CRATE_DOMAINS.find((domain) => (
        normalizedHostname === domain || normalizedHostname.endsWith(`.${domain}`)
    )) || null;
};

const isLocalMarketplaceHost = (hostname) => {
    const normalizedHostname = String(hostname ?? '').trim().toLowerCase();

    return normalizedHostname === 'localhost'
        || normalizedHostname === '127.0.0.1'
        || normalizedHostname.endsWith('.localhost');
};

const isQaMarketplaceHost = (hostname) => String(hostname ?? '').trim().toLowerCase().includes('user-qa')
    || String(hostname ?? '').trim().toLowerCase().includes('staging');

export const buildMarketplaceContentReturnUrl = (originUrl, windowObject = window) => {
    try {
        const parsedOriginUrl = new URL(String(originUrl ?? '').trim());
        if (!['http:', 'https:'].includes(parsedOriginUrl.protocol)) {
            return null;
        }

        const secondaryDomain = getSecondaryCrateDomain(parsedOriginUrl.hostname);
        if (!secondaryDomain) {
            return null;
        }

        const currentHostname = String(windowObject?.location?.hostname ?? '').trim().toLowerCase();
        if (isLocalMarketplaceHost(currentHostname)) {
            return null;
        }

        const destinationHostname = isQaMarketplaceHost(currentHostname)
            ? 'staging.productioncrate.com'
            : SECONDARY_CONTENT_HOSTS[secondaryDomain];

        if (!destinationHostname) {
            return null;
        }

        parsedOriginUrl.protocol = 'https:';
        parsedOriginUrl.hostname = destinationHostname;
        parsedOriginUrl.port = '';
        parsedOriginUrl.username = '';
        parsedOriginUrl.password = '';

        return parsedOriginUrl.toString();
    } catch (error) {
        return null;
    }
};

export const consumeMarketplaceCartReturnMarker = (windowObject = window) => {
    if (!windowObject?.location?.href || !windowObject?.history?.replaceState) {
        return false;
    }

    try {
        const currentUrl = new URL(windowObject.location.href);
        if (currentUrl.searchParams.get(MARKETPLACE_CART_RETURN_PARAM) !== '1') {
            return false;
        }

        currentUrl.searchParams.delete(MARKETPLACE_CART_RETURN_PARAM);
        const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
        windowObject.history.replaceState({}, windowObject.document?.title || '', nextUrl);
        return true;
    } catch (error) {
        return false;
    }
};

export const getCartLineKey = (line) => {
    if (line?.type === 'asset' && typeof line?.assetUuid === 'string' && line.assetUuid.trim()) {
        return `asset:${line.assetUuid.trim()}`;
    }

    if (line?.type === 'bundle') {
        const bundleId = parseInteger(line?.bundleId);
        if (isPositiveInteger(bundleId)) {
            return `bundle:${bundleId}`;
        }
    }

    return null;
};

export const getCartLineSignature = (line) => {
    const lineKey = getCartLineKey(line);

    if (!lineKey) {
        return null;
    }

    if (line?.type === 'asset') {
        return `${lineKey}:${parseInteger(line?.assetPriceId) || ''}`;
    }

    return lineKey;
};

export const normalizeCartDisplay = (display, type, sourceContext = {}) => {
    if (!display || typeof display !== 'object') {
        return null;
    }

    const name = String(display.name ?? '').trim();
    const amount = Number(display.amount);
    const currency = normalizeCurrency(display.currency);
    const typeLabel = TYPE_LABELS[type] || TYPE_LABELS.asset;
    const formattedAmount = formatCurrency(amount, currency);
    const priceLabel = String(display.priceLabel ?? '').trim() || formattedAmount;
    const vertical = resolveCartVertical(display, sourceContext);
    const previewUrl = resolveCartPreviewUrl(display);
    const waveformUrl = resolveCartWaveformUrl(display);

    return {
        name: name || 'Untitled item',
        amount: Number.isFinite(amount) ? amount : null,
        currency,
        priceLabel: priceLabel || null,
        typeLabel,
        vertical,
        previewUrl,
        waveformUrl,
    };
};

const normalizePersistedDisplay = (display, type, sourceContext = {}) => {
    if (!display || typeof display !== 'object') {
        return null;
    }

    const amount = Number(display.amount ?? display.a);
    const name = display.name ?? display.n;
    const vertical = display.vertical ?? display.v;
    const currencyCandidate = display.currency ?? display.c;
    const previewUrl = display.previewUrl ?? display.i;
    const waveformUrl = display.waveformUrl ?? display.f;

    return normalizeCartDisplay({
        name,
        amount,
        currency: Number.isFinite(amount)
            ? (currencyCandidate || DEFAULT_MARKETPLACE_CURRENCY)
            : currencyCandidate,
        vertical,
        previewUrl,
        waveformUrl,
    }, type, sourceContext);
};

const getAssetPriceSourceType = (input) => String(
    input.assetPriceSourceType
        ?? input.asset_price_source_type
        ?? input.priceSourceType
        ?? input.price_source_type
        ?? input.sourceType
        ?? input.source_type
        ?? input.defaultPrice?.source_type
        ?? input.default_price?.source_type
        ?? input.marketplace?.default_price?.source_type
        ?? ''
).trim().toLowerCase();

const getAssetPriceCandidate = (input) => (
    input.assetPriceId
        ?? input.asset_price_id
        ?? input.defaultPrice?.source_id
        ?? input.default_price?.source_id
        ?? input.marketplace?.default_price?.source_id
);

const hasExplicitAssetPriceSelection = (input) => Boolean(
    input.assetPriceExplicit
        || input.asset_price_explicit
        || input.explicitAssetPrice
        || input.explicit_asset_price
        || input.selectedAssetPrice
        || input.selected_asset_price
);

const normalizeAssetPriceId = (input) => {
    const assetPriceId = parseInteger(getAssetPriceCandidate(input));
    if (!isPositiveInteger(assetPriceId)) {
        return null;
    }

    if (getAssetPriceSourceType(input) === 'asset_price' || hasExplicitAssetPriceSelection(input)) {
        return assetPriceId;
    }

    return null;
};

export const normalizeAddItemInput = (input) => {
    if (!input || typeof input !== 'object') {
        return null;
    }

    const type = String(input.type ?? '').trim().toLowerCase();
    const nowIso = new Date().toISOString();

    if (type === 'asset') {
        const assetUuid = String(input.assetUuid ?? '').trim();
        if (!assetUuid) {
            return null;
        }

        const assetPriceId = normalizeAssetPriceId(input);
        const sourceContext = normalizeSourceContext(input.sourceContext);
        const assetHref = normalizeAssetHref(input.assetHref ?? input.asset_href ?? sourceContext.originUrl);
        const displaySourceContext = {
            ...sourceContext,
            assetHref,
            originUrl: sourceContext.originUrl || assetHref || '',
        };

        return {
            type: 'asset',
            assetUuid,
            assetPriceId,
            assetPriceSourceType: assetPriceId ? 'asset_price' : null,
            assetHref,
            bundleId: null,
            addedAt: nowIso,
            display: normalizeCartDisplay(input.display, 'asset', displaySourceContext),
            sourceContext,
        };
    }

    if (type === 'bundle') {
        const bundleId = parseInteger(input.bundleId);
        if (!isPositiveInteger(bundleId)) {
            return null;
        }

        const sourceContext = normalizeSourceContext(input.sourceContext);

        return {
            type: 'bundle',
            bundleId,
            assetUuid: null,
            assetPriceId: null,
            addedAt: nowIso,
            display: normalizeCartDisplay(input.display, 'bundle', sourceContext),
            sourceContext,
        };
    }

    return null;
};

export const normalizePersistedLine = (line, options = {}) => {
    if (!line || typeof line !== 'object') {
        return null;
    }

    const { sharedOrigin = null, sharedPreviewOrigin = null } = options;

    const typeToken = String(line.type ?? line.t ?? '').trim().toLowerCase();
    const type = typeToken === 'a' ? 'asset' : typeToken === 'b' ? 'bundle' : typeToken;
    const addedAt = normalizeIsoDate(line.addedAt);

    if (type === 'asset') {
        const assetUuid = String(line.assetUuid ?? line.u ?? '').trim();
        if (!assetUuid) {
            return null;
        }

        const assetPriceId = parseInteger(line.assetPriceId ?? line.p);
        const assetHref = expandAssetHref(line.assetHref ?? line.h, sharedOrigin);
        const nestedDisplay = line.display && typeof line.display === 'object'
            ? normalizePersistedDisplay({
                ...line.display,
                previewUrl: expandPreviewUrl(line.display?.previewUrl ?? line.display?.i, sharedPreviewOrigin),
                waveformUrl: expandPreviewUrl(line.display?.waveformUrl ?? line.display?.f, sharedPreviewOrigin),
            }, 'asset', { assetHref })
            : null;
        const compactDisplay = normalizePersistedDisplay({
            ...line,
            previewUrl: expandPreviewUrl(line.previewUrl ?? line.i, sharedPreviewOrigin),
            waveformUrl: expandPreviewUrl(line.waveformUrl ?? line.f, sharedPreviewOrigin),
        }, 'asset', { assetHref });

        return {
            type: 'asset',
            assetUuid,
            assetPriceId: isPositiveInteger(assetPriceId) ? assetPriceId : null,
            assetHref,
            bundleId: null,
            addedAt,
            display: nestedDisplay || compactDisplay,
        };
    }

    if (type === 'bundle') {
        const bundleId = parseInteger(line.bundleId ?? line.b);
        if (!isPositiveInteger(bundleId)) {
            return null;
        }

        const nestedDisplay = line.display && typeof line.display === 'object'
            ? normalizePersistedDisplay({
                ...line.display,
                previewUrl: expandPreviewUrl(line.display?.previewUrl ?? line.display?.i, sharedPreviewOrigin),
                waveformUrl: expandPreviewUrl(line.display?.waveformUrl ?? line.display?.f, sharedPreviewOrigin),
            }, 'bundle')
            : null;
        const compactDisplay = normalizePersistedDisplay({
            ...line,
            previewUrl: expandPreviewUrl(line.previewUrl ?? line.i, sharedPreviewOrigin),
            waveformUrl: expandPreviewUrl(line.waveformUrl ?? line.f, sharedPreviewOrigin),
        }, 'bundle');

        return {
            type: 'bundle',
            bundleId,
            assetUuid: null,
            assetPriceId: null,
            addedAt,
            display: nestedDisplay || compactDisplay,
        };
    }

    return null;
};

export const normalizeSourceContext = (sourceContext) => {
    if (!sourceContext || typeof sourceContext !== 'object') {
        return {
            explicitPurchase: false,
            originUrl: '',
            originDomain: '',
        };
    }

    return {
        explicitPurchase: Boolean(sourceContext.explicitPurchase),
        originUrl: String(sourceContext.originUrl ?? '').trim(),
        originDomain: String(sourceContext.originDomain ?? '').trim(),
    };
};

export const buildPersistedCartPayload = ({
    clientCartId,
    ownerUserId,
    revision,
    syncState,
    writerId,
    lines,
}) => {
    const normalizedLines = Array.isArray(lines) ? lines : [];
    const assetOrigins = normalizedLines
        .map((line) => line?.type === 'asset' ? getAssetUrlOrigin(line.assetHref) : null)
        .filter(Boolean);
    const sharedOrigin = assetOrigins.length > 0 && assetOrigins.every((origin) => origin === assetOrigins[0])
        ? assetOrigins[0]
        : null;
    const previewOrigins = normalizedLines
        .flatMap((line) => [
            getPreviewUrlOrigin(line?.display?.previewUrl),
            getPreviewUrlOrigin(line?.display?.waveformUrl),
        ])
        .filter(Boolean);
    const sharedPreviewOrigin = previewOrigins.length > 0 && previewOrigins.every((origin) => origin === previewOrigins[0])
        ? previewOrigins[0]
        : null;

    return {
        v: CART_COOKIE_VERSION,
        c: String(clientCartId ?? '').trim(),
        o: String(ownerUserId ?? '').trim() || undefined,
        r: normalizePersistedRevision(revision),
        w: normalizePersistedWriterId(writerId) || undefined,
        m: sharedOrigin || undefined,
        q: sharedPreviewOrigin || undefined,
        s: syncState === CART_SYNC_STATES.DIRTY
            ? 'd'
            : syncState === CART_SYNC_STATES.ERROR
                ? 'e'
                : undefined,
        l: normalizedLines.map((line) => {
        if (line?.type === 'asset') {
            const display = normalizeCartDisplay(line.display, 'asset');
            return {
                t: 'a',
                u: line.assetUuid,
                p: line.assetPriceId || undefined,
                h: compactAssetHref(line.assetHref, sharedOrigin),
                n: display?.name || undefined,
                a: Number.isFinite(Number(display?.amount)) ? Number(display.amount) : undefined,
                v: display?.vertical || undefined,
                i: compactPreviewUrl(display?.previewUrl, sharedPreviewOrigin),
                f: compactPreviewUrl(display?.waveformUrl, sharedPreviewOrigin),
            };
        }

        const display = normalizeCartDisplay(line.display, 'bundle');
        return {
            t: 'b',
            b: line.bundleId,
            n: display?.name || undefined,
            a: Number.isFinite(Number(display?.amount)) ? Number(display.amount) : undefined,
            v: display?.vertical || undefined,
            i: compactPreviewUrl(display?.previewUrl, sharedPreviewOrigin),
            f: compactPreviewUrl(display?.waveformUrl, sharedPreviewOrigin),
        };
        }),
    };
};

const normalizePersistedRevision = (value) => {
    const parsedRevision = parseInteger(value);

    if (!Number.isInteger(parsedRevision) || parsedRevision < 0) {
        return 0;
    }

    return parsedRevision;
};

const normalizePersistedWriterId = (value) => {
    const normalizedValue = String(value ?? '').trim();
    return normalizedValue || null;
};

const normalizePersistedOwnerUserId = (value) => {
    const normalizedValue = String(value ?? '').trim();

    if (!normalizedValue || normalizedValue.startsWith('http://') || normalizedValue.startsWith('https://')) {
        return null;
    }

    return normalizedValue;
};

const parseJsonCookiePayload = (rawValue) => JSON.parse(decodeURIComponent(rawValue));

const parseCompressedCookiePayload = (rawValue) => {
    const compressedValue = rawValue.slice(COMPRESSED_CART_COOKIE_PREFIX.length);
    const inflatedBytes = inflateSync(fromBase64Url(compressedValue));
    return JSON.parse(strFromU8(inflatedBytes));
};

export const encodeCartCookiePayload = (payload) => {
    const jsonPayload = JSON.stringify(payload);
    const compressedPayload = deflateSync(strToU8(jsonPayload));

    return `${COMPRESSED_CART_COOKIE_PREFIX}${toBase64Url(compressedPayload)}`;
};

export const decodeCartCookiePayload = (rawValue) => {
    if (typeof rawValue !== 'string' || !rawValue.trim()) {
        return null;
    }

    try {
        const parsedValue = rawValue.startsWith(COMPRESSED_CART_COOKIE_PREFIX)
            ? parseCompressedCookiePayload(rawValue)
            : parseJsonCookiePayload(rawValue);
        if (!parsedValue || typeof parsedValue !== 'object') {
            return null;
        }

        const parsedLines = parsedValue.lines ?? parsedValue.l;
        const sharedOrigin = getAssetUrlOrigin(parsedValue.origin ?? parsedValue.m ?? parsedValue.o);
        const sharedPreviewOrigin = getPreviewUrlOrigin(parsedValue.previewOrigin ?? parsedValue.q);
        const lines = Array.isArray(parsedLines)
            ? parsedLines.map((line) => normalizePersistedLine(line, { sharedOrigin, sharedPreviewOrigin })).filter(Boolean)
            : [];
        const parsedSyncState = parsedValue.syncState ?? parsedValue.s;
        const syncState = parsedSyncState === 'd'
            ? CART_SYNC_STATES.DIRTY
            : parsedSyncState === 'e'
                ? CART_SYNC_STATES.ERROR
                : Object.values(CART_SYNC_STATES).includes(parsedSyncState)
                    ? parsedSyncState
                    : CART_SYNC_STATES.CLEAN;

        return {
            version: parsedValue.version ?? parsedValue.v,
            clientCartId: String(parsedValue.clientCartId ?? parsedValue.c ?? '').trim(),
            updatedAt: parsedValue.updatedAt ? normalizeIsoDate(parsedValue.updatedAt) : null,
            revision: normalizePersistedRevision(parsedValue.revision ?? parsedValue.r),
            writerId: normalizePersistedWriterId(parsedValue.writerId ?? parsedValue.w),
            ownerUserId: normalizePersistedOwnerUserId(parsedValue.ownerUserId ?? parsedValue.o),
            syncState,
            lines,
        };
    } catch (error) {
        return null;
    }
};

export const calculateLocalSubtotal = (lines) => {
    const normalizedLines = Array.isArray(lines) ? lines : [];
    if (!normalizedLines.length) {
        return {
            amount: null,
            currency: null,
            formatted: null,
        };
    }

    let currency = null;
    let amount = 0;

    for (const line of normalizedLines) {
        const displayAmount = Number(line?.display?.amount);
        const displayCurrency = normalizeCurrency(line?.display?.currency);

        if (!Number.isFinite(displayAmount) || !displayCurrency) {
            return {
                amount: null,
                currency: null,
                formatted: null,
            };
        }

        if (!currency) {
            currency = displayCurrency;
        }

        if (currency !== displayCurrency) {
            return {
                amount: null,
                currency: null,
                formatted: null,
            };
        }

        amount += displayAmount;
    }

    return {
        amount,
        currency,
        formatted: formatCurrency(amount, currency),
    };
};

export const buildEmptyCartSnapshot = () => ({
    apiVersion: CART_API_VERSION,
    isReady: false,
    isOpen: false,
    clientCartId: '',
    revision: 0,
    updatedAt: null,
    syncState: CART_SYNC_STATES.CLEAN,
    isSessionResolved: false,
    isReconciling: false,
    itemCount: 0,
    lines: [],
    subtotal: {
        amount: null,
        currency: null,
        formatted: null,
    },
    mergeRequired: false,
    mergeComparison: null,
    lastError: null,
});

const getMarketplaceCartOrigin = (windowObject = window) => {
    const hostname = String(windowObject?.location?.hostname ?? '').trim().toLowerCase();

    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
        return 'http://localhost:3000';
    }

    if (hostname.includes('user-qa') || hostname.includes('staging')) {
        return 'https://user-qa.productioncrate.com';
    }

    return PRIMARY_MARKETPLACE_ORIGIN;
};

export const buildMarketplaceCartUrl = (pathname = '/marketplace/cart', windowObject = window) => {
    const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;
    return `${getMarketplaceCartOrigin(windowObject)}${normalizedPathname}`;
};
